import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
const ClientsPage = React.lazy(() => import("@/pages/ClientsPage"));
const ClientDetailPage = React.lazy(() => import("@/pages/ClientDetailPage"));
const IntelligencePage = React.lazy(() => import("@/pages/IntelligencePage"));
const IntelArticlePage = React.lazy(() => import("@/pages/IntelArticlePage"));
const IntelProfilePage = React.lazy(() => import("@/pages/IntelProfilePage"));
import AgendaPage from "@/pages/AgendaPage";
const PipelinePage = React.lazy(() => import("@/pages/PipelinePage"));
import ToolsPage from "@/pages/ToolsPage";
import ImportExportPage from "@/pages/ImportExportPage";
import PpQuotePage from "@/pages/PpQuotePage";
import WaTemplatesPage from "@/pages/WaTemplatesPage";
import FieldManagerPage from "@/pages/FieldManagerPage";
import CalendarPage from "@/pages/CalendarPage";
import ComparePage from "@/pages/ComparePage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import DedupPage from "@/pages/DedupPage";
const StatisticsPage = React.lazy(() => import("@/pages/StatisticsPage"));
const RecoveryPage = React.lazy(() => import("@/pages/RecoveryPage"));
const RecallPlanPage = React.lazy(() => import("@/pages/RecallPlanPage"));
const CoachPlaybookPage = React.lazy(() => import("@/pages/CoachPlaybookPage"));
const TeamAdminPage = React.lazy(() => import("@/pages/TeamAdminPage"));
const CampaignsPage = React.lazy(() => import("@/pages/CampaignsPage"));
const AuditPage = React.lazy(() => import("@/pages/AuditPage"));
const WhatsAppInboxPage = React.lazy(() => import("@/pages/WhatsAppInboxPage"));
const WhatsAppIntegrationPage = React.lazy(() => import("@/pages/WhatsAppIntegrationPage"));
const WhatsApp360dialogPage = React.lazy(() => import("@/pages/WhatsApp360dialogPage"));
const ProductionBoostPage = React.lazy(() => import("@/pages/ProductionBoostPage"));
const BoostHistoryPage = React.lazy(() => import("@/pages/BoostHistoryPage"));
const ArenaPage = React.lazy(() => import("@/pages/ArenaPage"));
import PortfoliosPage from "@/pages/PortfoliosPage";
import PortfolioActivePage from "@/pages/PortfolioActivePage";
import RejectReasonsPage from "@/pages/RejectReasonsPage";
import DprRecoveryPage from "@/pages/DprRecoveryPage";
const ComandantePage = React.lazy(() => import("@/pages/ComandantePage"));
import TeamPerformancePage from "@/pages/TeamPerformancePage";
import KnowledgePage from "@/pages/KnowledgePage";
const CrossSellPage = React.lazy(() => import("@/pages/CrossSellPage"));
const DirectoryPage = React.lazy(() => import("@/pages/DirectoryPage"));
import WorkspacesListPage from "@/pages/WorkspacesListPage";
const WorkspaceDetailPage = React.lazy(() => import("@/pages/WorkspaceDetailPage"));
const BoardPage = React.lazy(() => import("@/pages/BoardPage"));
const DocumentsPage = React.lazy(() => import("@/pages/DocumentsPage"));
import SidebarBuilderPage from "@/pages/SidebarBuilderPage";
const ProfilePage = React.lazy(() => import("@/pages/ProfilePage"));
// Mobile pages
import MobileToday from "@/pages/mobile/MobileToday";
import MobileClients from "@/pages/mobile/MobileClients";
import MobileClientDetail from "@/pages/mobile/MobileClientDetail";
import MobileComandante from "@/pages/mobile/MobileComandante";
import MobileAgenda from "@/pages/mobile/MobileAgenda";
import MobileMore from "@/pages/mobile/MobileMore";
import MobileAddClient from "@/pages/mobile/MobileAddClient";
import MobilePortfolios from "@/pages/mobile/MobilePortfolios";
import { useIsMobile } from "@/lib/useIsMobile";
import { useLocation, useNavigate, Navigate as Nav2 } from "react-router-dom";
import GlobalLoadingBar from "@/components/GlobalLoadingBar";
import VoiceQuickAction from "@/components/VoiceQuickAction";
import CopilotWidget from "@/components/CopilotWidget";
import NotFoundPage from "@/pages/NotFoundPage";
const VaultPage = React.lazy(() => import("@/pages/VaultPage"));
const LandingPage = React.lazy(() => import("@/pages/LandingPage"));
const LandingConsolidamento = React.lazy(() => import("@/pages/LandingConsolidamento"));
const LandingLeadsPage = React.lazy(() => import("@/pages/LandingLeadsPage"));
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { CookieBanner } from "./components/CookieBanner";
import { Suspense } from "react";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F2F5" }}>
        <div className="flex flex-col items-center gap-4">
          <img src="/brand/logo-mark.png" alt="Best Agent" className="w-10 h-10 rounded-[5px] object-cover" />
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--gold)" }} />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      {children}
      <VoiceQuickAction />
      <CopilotWidget />
    </>
  );
}

/** Auto-redirect mobile a /m al primo accesso (tranne se gia' su /m, /login, /register, /recupero, scheda cliente diretta).
 *  L'utente puo' sempre forzare la versione desktop dal menu "Più" → Apri versione desktop.
 */
function MobileAutoRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  if (!isMobile) return null;
  if (!user) return null;
  const path = location.pathname;
  // Skip se gia' su mobile o su pagine pubbliche/login/scheda specifica usata in deeplink
  if (path.startsWith("/m") || path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/recupero")) return null;
  // sessionStorage flag = utente ha esplicitamente scelto desktop
  if (typeof window !== "undefined" && sessionStorage.getItem("forceDesktop") === "1") return null;
  // Mappa rotte desktop → mobile equivalenti
  const map = {
    "/": "/m",
    "/clienti": "/m/clienti",
    "/agenda": "/m/agenda",
    "/comandante": "/m/comandante",
    "/portafogli": "/m/portafogli",
  };
  const target = map[path];
  if (target) return <Nav2 to={target + location.search} replace />;
  return null;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F2F5" }}>
        <div className="flex flex-col items-center gap-4">
          <img src="/brand/logo-mark.png" alt="Best Agent" className="w-10 h-10 rounded-[5px] object-cover" />
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--gold)" }} />
        </div>
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

function HomeGate() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F2F5" }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--gold)" }} />
      </div>
    );
  }
  if (!user) return <LandingPage />;
  return (
    <>
      <DashboardPage />
      <VoiceQuickAction />
      <CopilotWidget />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <CookieBanner />
        <GlobalLoadingBar />
        <MobileAutoRedirect />
        <ErrorBoundary>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center w-4 h-4 animate-spin" style={{ background: "#F0F2F5" }}><Loader2  style={{ color: "var(--gold)" }} /></div>}>
        <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
          <Route path="/recupero" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
          <Route path="/consolidamento" element={<LandingConsolidamento />} />

          {/* ============ MOBILE ROUTES ============ */}
          <Route path="/m" element={<Protected><MobileToday /></Protected>} />
          <Route path="/m/clienti" element={<Protected><MobileClients /></Protected>} />
          <Route path="/m/clienti/:id" element={<Protected><MobileClientDetail /></Protected>} />
          <Route path="/m/comandante" element={<Protected><MobileComandante /></Protected>} />
          <Route path="/m/agenda" element={<Protected><MobileAgenda /></Protected>} />
          <Route path="/m/aggiungi" element={<Protected><MobileAddClient /></Protected>} />
          <Route path="/m/portafogli" element={<Protected><MobilePortfolios /></Protected>} />
          <Route path="/m/piu" element={<Protected><MobileMore /></Protected>} />

          {/* ============ DESKTOP ROUTES ============ */}
          <Route path="/" element={<HomeGate />} />
          <Route path="/clienti" element={<Protected><ClientsPage /></Protected>} />
          <Route path="/boost/:sid" element={<Protected><ProductionBoostPage /></Protected>} />
          <Route path="/boost-storico" element={<Protected><BoostHistoryPage /></Protected>} />
          <Route path="/arena" element={<Protected><ArenaPage /></Protected>} />
          <Route path="/lead-ads" element={<Protected><LandingLeadsPage /></Protected>} />
          <Route path="/clienti/:id" element={<Protected><ClientDetailPage /></Protected>} />
          <Route path="/agenda" element={<Protected><AgendaPage /></Protected>} />
          <Route path="/calendario" element={<Protected><CalendarPage /></Protected>} />
          <Route path="/pipeline" element={<Protected><PipelinePage /></Protected>} />
          <Route path="/preventivatore" element={<Protected><PpQuotePage /></Protected>} />
          <Route path="/strumenti" element={<Protected><ToolsPage /></Protected>} />
          <Route path="/messaggi" element={<Protected><WaTemplatesPage /></Protected>} />
          <Route path="/campi" element={<Protected><FieldManagerPage /></Protected>} />
          <Route path="/confronto" element={<Protected><ComparePage /></Protected>} />
          <Route path="/coach" element={<Protected><CoachPlaybookPage /></Protected>} />
          <Route path="/team" element={<Protected><TeamAdminPage /></Protected>} />
          <Route path="/team-legacy" element={<Protected><AdminUsersPage /></Protected>} />
          <Route path="/campagne" element={<Protected><CampaignsPage /></Protected>} />
          <Route path="/audit" element={<Protected><AuditPage /></Protected>} />
          <Route path="/inbox" element={<Protected><WhatsAppInboxPage /></Protected>} />
          <Route path="/integrazioni/whatsapp" element={<Protected><WhatsAppIntegrationPage /></Protected>} />
          <Route path="/integrazioni/360dialog" element={<Protected><WhatsApp360dialogPage /></Protected>} />
          <Route path="/integrazioni" element={<Protected><IntegrationsPage /></Protected>} />
          <Route path="/duplicati" element={<Protected><DedupPage /></Protected>} />
          <Route path="/statistiche" element={<Protected><StatisticsPage /></Protected>} />
          <Route path="/cronologia" element={<Protected><RecoveryPage /></Protected>} />
          <Route path="/piano-recall" element={<Protected><RecallPlanPage /></Protected>} />
          <Route path="/dati" element={<Protected><ImportExportPage /></Protected>} />
          <Route path="/portafogli" element={<Protected><PortfoliosPage /></Protected>} />
          <Route path="/portafoglio-attivo" element={<Protected><PortfolioActivePage /></Protected>} />
          <Route path="/impostazioni/motivi-rifiuto" element={<Protected><RejectReasonsPage /></Protected>} />
          <Route path="/strumenti/recupero-dpr" element={<Protected><DprRecoveryPage /></Protected>} />
          <Route path="/comandante" element={<Protected><ComandantePage /></Protected>} />
          <Route path="/team-performance" element={<Protected><TeamPerformancePage /></Protected>} />
          <Route path="/conoscenza" element={<Protected><KnowledgePage /></Protected>} />
          <Route path="/crosssell" element={<Protected><CrossSellPage /></Protected>} />
          <Route path="/glossario" element={<Protected><DirectoryPage /></Protected>} />
          <Route path="/aree-di-lavoro" element={<Protected><WorkspacesListPage /></Protected>} />
          <Route path="/aree-di-lavoro/:workspaceId" element={<Protected><WorkspaceDetailPage /></Protected>} />
          <Route path="/board/:boardId" element={<Protected><BoardPage /></Protected>} />
          <Route path="/documenti" element={<Protected><DocumentsPage /></Protected>} />
          <Route path="/impostazioni/sidebar" element={<Protected><SidebarBuilderPage /></Protected>} />
          <Route path="/profilo" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/intelligence" element={<Protected><IntelligencePage /></Protected>} />
          <Route path="/intelligence/articolo/:id" element={<Protected><IntelArticlePage /></Protected>} />
          <Route path="/intelligence/profilo" element={<Protected><IntelProfilePage /></Protected>} />
          <Route path="/vault" element={<Protected><VaultPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}
